import {
  BookingChartDataDTO,
  GroupedBarChartData,
  PieChartData,
  statusConverter,
} from '@bookly/shared';

export function countPerMonthRT(
  bookings: BookingChartDataDTO[]
): GroupedBarChartData[] {
  const roomTypes = Array.from(
    new Set(bookings.map(b => b.roomType ?? 'Unknown'))
  );
  // Group bookings by month and room type
  const grouped = new Map<string, Record<string, number>>();

  for (const b of bookings) {
    const month = new Date(0, b.month - 1).toLocaleString('en-US', {
      month: 'long',
    });
    const roomType = b.roomType ?? 'Unknown';

    if (!grouped.has(month)) {
      grouped.set(month, {});
    }
    grouped.get(month)![roomType] = (grouped.get(month)![roomType] ?? 0) + 1;
  }

  return [...grouped.entries()]
    .sort(
      ([a], [b]) =>
        new Date(`${a} 1`).getMonth() - new Date(`${b} 1`).getMonth()
    )
    .map(([month, data]) => {
      const record: Record<string, number> = {};
      for (const rt of roomTypes) {
        record[rt] = data[rt] ?? 0;
      }
      return { group: month, ...record };
    });
}

export function averagePerWeekday(
  bookings: BookingChartDataDTO[]
): GroupedBarChartData[] {
  const weekdays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  const bookingCounts = Object.fromEntries(weekdays.map(day => [day, 0]));

  if (bookings.length === 0) {
    return [{ group: 'weekdays', ...bookingCounts }];
  }

  // bookings are sorted by startTime descending, so the first booking is the most recent
  const minDate = new Date(bookings[bookings.length - 1].startTime);
  const maxDate = new Date(bookings[0].startTime);

  // Count bookings per weekday
  for (const b of bookings) {
    const weekday = new Date(b.startTime).toLocaleDateString('en-US', {
      weekday: 'long',
    });
    if (weekday in bookingCounts) bookingCounts[weekday]++;
  }

  // Count occurrences of each weekday in the date range
  const weekdayOccurrences = Object.fromEntries(weekdays.map(day => [day, 0]));
  for (
    let d = new Date(minDate.getTime());
    d <= maxDate;
    d.setDate(d.getDate() + 1)
  ) {
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    if (weekday in weekdayOccurrences) weekdayOccurrences[weekday]++;
  }

  // Calculate averages
  const averages = Object.fromEntries(
    weekdays.map(day =>
      weekdayOccurrences[day]
        ? [day, +(bookingCounts[day] / weekdayOccurrences[day]).toFixed(2)]
        : [day, 0]
    )
  );

  return [{ group: 'weekdays', ...averages }];
}

export function averagePeoplePerRT(
  bookings: BookingChartDataDTO[]
): GroupedBarChartData[] {
  const grouped = new Map<string, { people: number; count: number }>();

  for (const b of bookings) {
    const roomType = b.roomType ?? 'Unknown';
    if (!grouped.has(roomType)) grouped.set(roomType, { people: 0, count: 0 });
    const stats = grouped.get(roomType)!;
    stats.people += b.numberOfPeople;
    stats.count++;
  }

  return [...grouped.entries()].map(([roomType, stats]) => ({
    group: roomType,
    'Avg number of people': stats.count
      ? Math.round(stats.people / stats.count)
      : 0,
  }));
}

export function countPerStatus(
  bookings: BookingChartDataDTO[]
): PieChartData[] {
  const statusCounts = new Map<string, number>();

  for (const b of bookings) {
    const status = statusConverter(b.status, b.isReapproved).text ?? 'Unknown';
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
  }

  return [...statusCounts.entries()].map(([label, value]) => ({
    label,
    value,
  }));
}

export function countPerRoomLastWeek(
  bookings: BookingChartDataDTO[]
): GroupedBarChartData[] {
  const now = new Date();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(now.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });

  const grouped = new Map<string, Record<string, number>>();

  for (const day of days) {
    grouped.set(day, {});
  }

  for (const b of bookings) {
    const day = new Date(b.startTime).toISOString().slice(0, 10);
    if (grouped.has(day)) {
      const room = b.roomName ?? 'Unknown';
      grouped.get(day)![room] = (grouped.get(day)![room] ?? 0) + 1;
    }
  }

  return days.map(day => ({ group: day, ...grouped.get(day)! }));
}
